import Axios from '@/lib/axiosConfig';
import TokenService from '@/lib/TokenService';
import { ProfileStatus } from '@/constants/profileStatus';

/**
 * After a successful Cognito sign-in, verify the account isn't permanently
 * suspended BEFORE letting the user into the app, so a banned user never
 * flashes a protected page.
 *
 * A permanently-suspended user is signalled by the backend in either of two
 * ways, and we handle both:
 *   1. 403 on any authenticated request (authenticateToken blocks them), or
 *   2. `/profiles/my` returns 200 with status === PermanentlySuspended.
 *
 * We refresh the token first because the request interceptor reads it from a
 * cache that can still be stale/empty immediately after sign-in — without this
 * the probe could go out unauthenticated, miss the 403, and let the user in.
 */
export async function isAccountPermanentlySuspended(): Promise<boolean> {
  try {
    await TokenService.refreshTokens();
    const res = await Axios.get('/profiles/my');
    const status = (res?.data?.responseObject as { status?: number } | null)
      ?.status;
    return status === ProfileStatus.PermanentlySuspended;
  } catch (err: unknown) {
    const code = (err as { response?: { status?: number } })?.response?.status;
    return code === 403;
  }
}
